import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Badge from '../ui/Badge';
import { blogService, AnalyticsOverview, BlogPost } from '../../services/blogService';
import { FileText, Send, Calendar, Edit3, TrendingUp, Activity, Globe, Sparkles, ArrowUpRight, Gauge, ExternalLink } from 'lucide-react';

interface Props {
  onNavigate?: (tab: string) => void;
}

type BadgeVariant = 'primary' | 'success' | 'warning' | 'danger' | 'gray';

const statusConfig: Record<BlogPost['status'], { labelKey: string; labelFallback: string; variant: BadgeVariant }> = {
  draft: { labelKey: 'blogSeo.overview.statusDraft', labelFallback: 'Rascunho', variant: 'gray' },
  scheduled: { labelKey: 'blogSeo.overview.statusScheduled', labelFallback: 'Agendado', variant: 'warning' },
  publishing: { labelKey: 'blogSeo.overview.statusPublishing', labelFallback: 'Publicando', variant: 'primary' },
  published: { labelKey: 'blogSeo.overview.statusPublished', labelFallback: 'Publicado', variant: 'success' },
  failed: { labelKey: 'blogSeo.overview.statusFailed', labelFallback: 'Falhou', variant: 'danger' },
  archived: { labelKey: 'blogSeo.overview.statusArchived', labelFallback: 'Arquivado', variant: 'gray' },
};

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const gaugeColor = (score: number): string => {
  if (score >= 80) return '#22c55e';
  if (score >= 50) return '#eab308';
  return '#ef4444';
};

const seoPillClasses = (score: number): string => {
  if (score >= 80) return 'bg-green-100 text-green-800';
  if (score >= 50) return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

interface StatCardProps {
  icon: React.ReactNode;
  iconWrap: string;
  label: string;
  value: number;
}

const StatCard: React.FC<StatCardProps> = ({ icon, iconWrap, label, value }) => (
  <Card padding={false} className="flex items-center gap-3 p-3 sm:gap-4 sm:p-6">
    <div className={`flex h-9 w-9 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-lg sm:rounded-xl [&>svg]:h-5 [&>svg]:w-5 sm:[&>svg]:h-6 sm:[&>svg]:w-6 ${iconWrap}`}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-none">{value}</p>
      <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">{label}</p>
    </div>
  </Card>
);

const BlogOverview: React.FC<Props> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      try {
        const [analyticsRes, postsRes] = await Promise.all([
          blogService.getAnalytics(),
          blogService.listPosts({ limit: 5 }),
        ]);
        if (!active) return;
        setAnalytics(analyticsRes?.data ?? null);
        setRecentPosts(Array.isArray(postsRes?.data) ? postsRes.data : []);
      } catch (err) {
        console.error('Erro ao carregar visão geral do blog:', err);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const go = (tab: string) => {
    if (onNavigate) onNavigate(tab);
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('blogSeo.overview.loading', 'Carregando visão geral…')}</p>
        </div>
      </div>
    );
  }

  const posts = analytics?.posts;
  const total = posts?.total ?? 0;
  const published = posts?.published ?? 0;
  const scheduled = posts?.scheduled ?? 0;
  const drafts = posts?.drafts ?? 0;
  const avgSeoScore = Math.max(0, Math.min(100, Math.round(analytics?.avgSeoScore ?? 0)));
  const connections = analytics?.connections ?? 0;
  const flux = analytics?.flux;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const dash = (avgSeoScore / 100) * circumference;

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-br from-primary-600 via-purple-600 to-indigo-700 p-4 sm:p-8 text-white shadow-lg">
        <div className="flex flex-col gap-4 sm:gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h1 className="text-xl sm:text-3xl font-bold tracking-tight">{t('blogSeo.overview.title', 'Blog & SEO')}</h1>
            <p className="mt-1 sm:mt-2 text-sm sm:text-base text-white/80">
              {t('blogSeo.overview.subtitle', 'Controle total do seu blog e da sua estratégia de SEO em um só lugar.')}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              variant="ghost"
              className="bg-white/15 text-white backdrop-blur hover:bg-white/25 whitespace-nowrap !px-3 sm:!px-4 !text-xs sm:!text-sm"
              leftIcon={<Sparkles className="h-4 w-4" />}
              onClick={() => go('engine')}
            >
              {t('blogSeo.overview.generateContent', 'Gerar conteúdo')}
            </Button>
            <Button
              variant="ghost"
              className="bg-white/15 text-white backdrop-blur hover:bg-white/25 whitespace-nowrap !px-3 sm:!px-4 !text-xs sm:!text-sm"
              leftIcon={<Gauge className="h-4 w-4" />}
              onClick={() => go('seo')}
            >
              {t('blogSeo.overview.auditSite', 'Auditar site')}
            </Button>
            <Button
              variant="ghost"
              className="bg-white/10 text-white backdrop-blur hover:bg-white/20 whitespace-nowrap !px-3 sm:!px-4 !text-xs sm:!text-sm"
              leftIcon={<FileText className="h-4 w-4" />}
              onClick={() => go('posts')}
            >
              {t('blogSeo.overview.viewPosts', 'Ver posts')}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-2 sm:gap-4 md:grid-cols-4">
        <StatCard
          label={t('blogSeo.overview.statTotalPosts', 'Total de posts')}
          value={total}
          icon={<FileText className="h-6 w-6 text-primary-600 dark:text-primary-400" />}
          iconWrap="bg-primary-100 dark:bg-primary-900/30"
        />
        <StatCard
          label={t('blogSeo.overview.statPublished', 'Publicados')}
          value={published}
          icon={<Send className="h-6 w-6 text-green-600 dark:text-green-400" />}
          iconWrap="bg-green-100 dark:bg-green-900/30"
        />
        <StatCard
          label={t('blogSeo.overview.statScheduled', 'Agendados')}
          value={scheduled}
          icon={<Calendar className="h-6 w-6 text-amber-600 dark:text-amber-400" />}
          iconWrap="bg-amber-100 dark:bg-amber-900/30"
        />
        <StatCard
          label={t('blogSeo.overview.statDrafts', 'Rascunhos')}
          value={drafts}
          icon={<Edit3 className="h-6 w-6 text-gray-600 dark:text-gray-300" />}
          iconWrap="bg-gray-100 dark:bg-gray-700/60"
        />
      </div>

      {/* Second row */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* SEO médio */}
        <Card className="flex flex-col items-center justify-center text-center">
          <div className="relative h-36 w-36">
            <svg className="h-full w-full -rotate-90" viewBox="0 0 120 120">
              <circle
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                strokeWidth="10"
                className="stroke-gray-200 dark:stroke-gray-700"
              />
              <circle
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                strokeWidth="10"
                strokeLinecap="round"
                stroke={gaugeColor(avgSeoScore)}
                strokeDasharray={`${dash} ${circumference}`}
                style={{ transition: 'stroke-dasharray 0.6s ease' }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-gray-900 dark:text-white">{avgSeoScore}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">/ 100</span>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
            <TrendingUp className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            {t('blogSeo.overview.avgSeoScore', 'Pontuação SEO média')}
          </div>
        </Card>

        {/* Conexões */}
        <Card className="flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 dark:bg-primary-900/30">
                <Globe className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('blogSeo.overview.connections', 'Conexões')}</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white leading-none">{connections}</p>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
              {t('blogSeo.overview.connectionsHint', 'Integrações ativas para publicar e sincronizar conteúdo.')}
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            className="mt-4 self-start dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
            rightIcon={<ArrowUpRight className="h-4 w-4" />}
            onClick={() => go('connections')}
          >
            {t('blogSeo.overview.manage', 'Gerenciar')}
          </Button>
        </Card>

        {/* Flux */}
        <Card className="flex flex-col justify-between">
          {flux?.enabled ? (
            <>
              <div>
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-green-500" />
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">{t('blogSeo.overview.fluxActive', 'Flux ativo')}</span>
                </div>
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {flux?.hint || t('blogSeo.overview.fluxActiveHint', 'Seu painel Flux está conectado e operando.')}
                </p>
              </div>
              <a
                href={flux?.dashboardUrl || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 self-start rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-green-700"
              >
                {t('blogSeo.overview.openDashboard', 'Abrir painel')}
                <ExternalLink className="h-4 w-4" />
              </a>
            </>
          ) : (
            <>
              <div>
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">{t('blogSeo.overview.fluxInactive', 'Flux inativo')}</span>
                </div>
                <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                  {/* o texto do servidor é técnico (snippet/<head>); aqui fica a explicação simples */}
                  {t('blogSeo.overview.fluxInactiveHint', 'Conecte o Flux para automatizar publicações e analytics.')}
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                className="mt-4 self-start dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-700"
                rightIcon={<ArrowUpRight className="h-4 w-4" />}
                onClick={() => go('connections')}
              >
                {t('blogSeo.overview.connectFlux', 'Conectar Flux')}
              </Button>
            </>
          )}
        </Card>
      </div>

      {/* Recent posts */}
      <Card>
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('blogSeo.overview.recentPosts', 'Posts recentes')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('blogSeo.overview.recentPostsHint', 'Seus últimos conteúdos criados.')}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="dark:text-gray-300 dark:hover:bg-gray-700"
            rightIcon={<ArrowUpRight className="h-4 w-4" />}
            onClick={() => go('posts')}
          >
            {t('blogSeo.overview.viewAll', 'Ver todos')}
          </Button>
        </div>

        {recentPosts.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-200 px-6 py-12 text-center dark:border-gray-700">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700/60">
              <FileText className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{t('blogSeo.overview.emptyTitle', 'Nenhum post ainda')}</p>
            <p className="max-w-xs text-sm text-gray-500 dark:text-gray-400">
              {t('blogSeo.overview.emptyHint', 'Comece gerando seu primeiro conteúdo com a engine de IA.')}
            </p>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Sparkles className="h-4 w-4" />}
              onClick={() => go('engine')}
            >
              {t('blogSeo.overview.generateContent', 'Gerar conteúdo')}
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {recentPosts.map((post) => {
              const cfg = statusConfig[post.status] ?? { labelKey: '', labelFallback: post.status, variant: 'gray' as BadgeVariant };
              const statusLabel = cfg.labelKey ? t(cfg.labelKey, cfg.labelFallback) : cfg.labelFallback;
              const score = post.seo_score ?? 0;
              const dateValue = post.published_at ?? post.scheduled_at ?? post.created_at;
              return (
                <li key={post.id} className="flex items-center gap-3 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {post.title || t('blogSeo.overview.untitled', 'Sem título')}
                      </p>
                      {post.external_url && (
                        <a
                          href={post.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="shrink-0 text-gray-400 hover:text-primary-600"
                          aria-label={t('blogSeo.overview.openPost', 'Abrir post')}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2">
                      <Badge variant="gray" size="sm">{post.kind}</Badge>
                      <Badge variant={cfg.variant} size="sm">{statusLabel}</Badge>
                      <span className="text-xs text-gray-400 dark:text-gray-500">{formatDate(dateValue)}</span>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${seoPillClasses(score)}`}
                    title={t('blogSeo.overview.seoScoreTitle', 'Pontuação SEO')}
                  >
                    {t('blogSeo.overview.seoPill', 'SEO {{count}}', { count: score })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
};

export default BlogOverview;
